import { cn, cva } from 'noddi-ui-common';
import { formatDecimal } from 'noddi-util';
import { webButtonStyling } from '../../../helpers/webButtonStyling';
import type { StatusData } from './DashboardPieChart';

const rowVariants = cva('grid w-full grid-cols-[1fr_auto_3.5rem] items-center gap-4 py-3 text-left text-sm', {
  variants: {
    interactive: {
      true: cn(webButtonStyling, 'rounded-md hover:bg-muted'),
      false: ''
    }
  }
});

interface DistributionItem<T extends string | number> extends StatusData<T> {
  /** Subtitle under the name, e.g. what the category counts. */
  description?: string;
}

interface DashboardDistributionBarProps<T extends string | number = string | number> {
  data: DistributionItem<T>[];
  /** Makes each legend row a button, e.g. to open the list filtered to that status. */
  onItemClick?: (value: T) => void;
  emptyText?: string;
  /** Formats each row's amount, e.g. as currency. Counts by default. */
  valueFormatter?: (value: number) => string;
  className?: string;
}

/**
 * How a total splits across a few categories: one stacked bar, then a row per category with its count and share.
 * Reads like `DashboardPieChart`, but fits a fixed-height card and lines categories up for comparison.
 */
export const DashboardDistributionBar = <T extends string | number = string | number>({
  data,
  onItemClick,
  emptyText = 'No data for this period',
  valueFormatter = (value) => formatDecimal(value, 0),
  className
}: DashboardDistributionBarProps<T>) => {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  if (total === 0) {
    return <p className={cn('text-sm text-muted-foreground', className)}>{emptyText}</p>;
  }

  const share = (count: number) => (count / total) * 100;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className='flex h-2.5 gap-0.5 overflow-hidden rounded-full'>
        {data.map((item) => (
          <div
            key={item.value}
            className='min-w-1 shrink-0'
            style={{ width: `${share(item.count)}%`, backgroundColor: item.color }}
          />
        ))}
      </div>
      <ul className='flex flex-col'>
        {data.map((item) => {
          const row = (
            <>
              <span className='flex min-w-0 items-start gap-2'>
                <span className='mt-1 size-2.5 shrink-0 rounded-sm' style={{ backgroundColor: item.color }} />
                <span className='flex min-w-0 flex-col'>
                  <span className='truncate'>{item.name}</span>
                  {item.description && <span className='text-xs text-muted-foreground'>{item.description}</span>}
                </span>
              </span>
              <span className='font-semibold tabular-nums'>{valueFormatter(item.count)}</span>
              <span className='text-right text-xs text-muted-foreground tabular-nums'>
                {formatDecimal(share(item.count), 1)}%
              </span>
            </>
          );
          return (
            <li key={item.value} className='border-t'>
              {onItemClick ? (
                <button
                  type='button'
                  onClick={() => onItemClick(item.value)}
                  className={rowVariants({ interactive: true })}
                >
                  {row}
                </button>
              ) : (
                <div className={rowVariants({ interactive: false })}>{row}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
